import { useLayoutEffect, useRef, useState } from "react";
import { PMPagination } from "../../utils/PMPagination";
import { useNavigate } from "react-router-dom";
import { LoadingRows, dateToString } from "../../Utils";
import { ResourceEmpty } from "../../utils/ResourceEmpty";
import { Notes } from "../notes/notes";
import { useSettings } from "../../../contexts/settings-context";
import { useTranslation } from "react-i18next";
import {
  OfferStatus,
  normalizeOfferStatus,
  resolveOfferSalesAmount,
  resolveOfferCostAmount,
  parseMoneyAmount,
} from "./utils";

// Aggregate slim stats-strip figures from the current page of offers.
const calculateStatistics = (offers) => {
  let totalSales = 0;
  let totalCost = 0;
  let sentCount = 0;
  let acceptedCount = 0;
  const totalCount = offers?.length || 0;

  if (offers && offers.length > 0) {
    offers.forEach((offer) => {
      const statusKey = normalizeOfferStatus(offer.status);
      if (statusKey === "sent") sentCount++;
      if (statusKey === "accepted") acceptedCount++;

      totalSales += resolveOfferSalesAmount(offer);
      const cachedCost = resolveOfferCostAmount(offer);
      if (cachedCost != null) totalCost += cachedCost;
    });
  }

  const marginPercent =
    totalSales > 0 ? ((totalSales - totalCost) / totalSales) * 100 : 0;

  return { totalSales, totalCount, sentCount, acceptedCount, marginPercent };
};

const StatChip = ({ label, value, valueClassName = "text-slate-900" }) => (
  <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm font-semibold tabular-nums ${valueClassName}`}>
      {value}
    </span>
  </div>
);

const HEADER_CELL =
  "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500";
const HEADER_CELL_RIGHT = HEADER_CELL.replace("text-left", "text-right");
const HEADER_CELL_CENTER = HEADER_CELL.replace("text-left", "text-center");

const EMPTY = "—";
const COL_COUNT = 11;

/**
 * Prefer a real revision_number from chips / list attrs — never a raw DB id.
 */
function resolveListRevisionNumber(offer) {
  if (!offer) return null;

  const embedded =
    offer.current_draft_revision?.revision_number ??
    offer.accepted_revision?.revision_number ??
    offer.current_draft_revision_number ??
    offer.accepted_revision_number;
  if (embedded != null) return embedded;

  if (offer.revision_number != null) return offer.revision_number;
  if (offer.version != null) return offer.version;

  return null;
}

function resolveResponsibleName(offer) {
  const employee = offer?.our_contact_employee;
  if (!employee) return EMPTY;
  return (
    employee.displayTitle ||
    employee.name ||
    [employee.first_name, employee.last_name].filter(Boolean).join(" ") ||
    EMPTY
  );
}

export const OffersTable = (props) => {
  const {
    itemState,
    onPageChange,
    itemsCount,
    page,
    perPage,
    currentCount,
    onItemsSelect,
    featureFlags,
  } = props;
  const { data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const navigate = useNavigate();

  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const statistics = calculateStatistics(data);
  const hasRows = Array.isArray(data) && data.length > 0;

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems?.length > 0 && selectedItems?.length < data?.length;
    setChecked(selectedItems?.length === data?.length && data?.length > 0);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    if (onItemsSelect) {
      onItemsSelect(selectedItems);
    }
  }, [selectedItems, data?.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : data);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const openOffer = (item) => navigate("/dashboard/offers/" + item?.id);

  const OfferRow = ({ item }) => {
    const isSelected = selectedItems.includes(item);
    const sales = resolveOfferSalesAmount(item);
    const marginPercent = parseMoneyAmount(item?.contribution_margin_percent);
    // Claude: primary = title/name, secondary = offer number (muted)
    const primaryTitle =
      item?.title ||
      item?.name ||
      item?.reference ||
      (item?.id
        ? t("offers.list.offer_fallback", { id: item.id })
        : EMPTY);
    const offerNumber = item?.offer_number || null;
    const revisionNumber = resolveListRevisionNumber(item);

    return (
      <tr
        onClick={() => openOffer(item)}
        className={`cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50 ${
          isSelected ? "bg-blue-50/60" : ""
        }`}
      >
        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            value={item.id}
            checked={isSelected}
            onChange={(e) => {
              setSelectedItems(
                e.target.checked
                  ? [...selectedItems, item]
                  : selectedItems.filter((p) => p !== item)
              );
            }}
          />
        </td>
        <td className="px-3 py-2.5">
          <div className="font-semibold text-slate-900">{primaryTitle}</div>
          {offerNumber ? (
            <div className="truncate text-[11.5px] text-slate-400">
              {offerNumber}
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2.5 text-slate-700">
          {item?.customer?.displayTitle || item?.customer?.name || EMPTY}
        </td>
        <td className="px-3 py-2.5 text-slate-700">
          {resolveResponsibleName(item)}
        </td>
        <td className="px-3 py-2.5">
          <OfferStatus offer={item} />
        </td>
        <td className="px-3 py-2.5 text-center tabular-nums text-slate-500">
          {revisionNumber != null ? revisionNumber : EMPTY}
        </td>
        <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">
          {priceFormatter.format(sales)}
        </td>
        <td className="px-3 py-2.5 text-right tabular-nums">
          {marginPercent != null ? (
            <span
              className={
                marginPercent >= 0 ? "text-slate-700" : "text-red-600"
              }
            >
              {marginPercent.toFixed(1)}%
            </span>
          ) : (
            <span className="text-slate-400">{EMPTY}</span>
          )}
        </td>
        <td className="px-3 py-2.5 text-slate-600">
          {item?.valid_to ? dateToString(item.valid_to) : EMPTY}
        </td>
        <td className="px-3 py-2.5 text-slate-400">
          {item?.updated_at ? dateToString(item.updated_at) : EMPTY}
        </td>
        <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
          <Notes module="offer" module_param={item.id} />
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Slim stats strip */}
      {hasRows && (
        <div className="flex flex-wrap items-center gap-2">
          <StatChip
            label={t("offers.stats.total_sales")}
            value={priceFormatter.format(statistics.totalSales)}
          />
          <StatChip
            label={t("offers.stats.count")}
            value={statistics.totalCount}
          />
          <StatChip
            label={t("offers.stats.sent")}
            value={statistics.sentCount}
          />
          <StatChip
            label={t("offers.stats.accepted")}
            value={statistics.acceptedCount}
          />
          <StatChip
            label={t("offers.stats.margin")}
            value={`${statistics.marginPercent.toFixed(1)}%`}
            valueClassName={
              statistics.marginPercent >= 0 ? "text-slate-900" : "text-red-600"
            }
          />
        </div>
      )}

      {/* Selection bar */}
      {selectedItems?.length > 0 && (
        <div className="flex h-11 items-center rounded-xl bg-slate-900 px-5 text-white">
          <span className="text-sm font-medium">
            {selectedItems.length} {t("selected")}
          </span>
        </div>
      )}

      {/* Dense table card — Claude column set */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)] md:rounded-[18px]">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className={`${HEADER_CELL} w-10`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    ref={checkbox}
                    checked={checked}
                    onChange={toggleAll}
                  />
                </th>
                <th className={HEADER_CELL}>
                  {t("offers.list.columns.offer")}
                </th>
                <th className={HEADER_CELL}>
                  {t("offers.list.columns.customer")}
                </th>
                <th className={HEADER_CELL}>
                  {t("offers.list.columns.responsible")}
                </th>
                <th className={HEADER_CELL}>
                  {t("offers.list.columns.status")}
                </th>
                <th className={HEADER_CELL_CENTER}>
                  {t("offers.list.columns.revision")}
                </th>
                <th className={HEADER_CELL_RIGHT}>
                  {t("offers.list.columns.sum")}
                </th>
                <th className={HEADER_CELL_RIGHT}>
                  {t("offers.list.columns.margin")}
                </th>
                <th className={HEADER_CELL}>{t("offers.list.valid_to")}</th>
                <th className={HEADER_CELL}>
                  {t("offers.list.columns.updated")}
                </th>
                <th className={`${HEADER_CELL_RIGHT} w-10`} />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <LoadingRows cols={COL_COUNT - 1} checkboxCell />
                  <LoadingRows cols={COL_COUNT - 1} checkboxCell />
                  <LoadingRows cols={COL_COUNT - 1} checkboxCell />
                </>
              ) : errors ? (
                <tr>
                  <td
                    colSpan={COL_COUNT}
                    className="px-3 py-8 text-center text-red-500"
                  >
                    {t("offers.list.error")}
                  </td>
                </tr>
              ) : hasRows ? (
                data.map((item) => <OfferRow key={item?.id} item={item} />)
              ) : (
                <tr>
                  <td colSpan={COL_COUNT} className="px-3 py-6">
                    <ResourceEmpty text={t("offers.list.empty")} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={itemsCount}
        currentCount={currentCount}
        showCount={true}
      />
    </div>
  );
};

OffersTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
  onItemsSelect: () => {},
};
